import { useRef, useState, type ReactNode } from "react";
import { ConfirmDialog } from "./ConfirmDialog";
import { ApiError, type ApiClient, type LoadCheckReport, type LoadCommitResult, type LastLoad } from "@app/portal-sdk";
import { Alert, Box, Button, DataTable, EmptyState, PageHeader, RowState, SectionCard, Stack, Typography } from "@app/ui-kit";
import { formatDate } from "../lib/format";
import { errorMessage, useApiQuery } from "../lib/useApiQuery";

const XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
type Phase = "idle" | "checking" | "passed" | "failed" | "committing" | "committed" | "replacing" | "replaced" | "refused" | "error";
/**
 * The intake module refuses a file whole, with a sentence, before any row is read: a required sheet missing,
 * a forbidden column (CIN-1 criterion 4). Nothing went wrong; the file was not accepted for a stated reason,
 * and the chip must say that, or a generic failure over a precise message teaches people to distrust the message.
 */
const REFUSED = new Set(["bad_workbook", "forbidden_column"]);
const phaseFor = (e: unknown): Phase => (e instanceof ApiError && e.code !== undefined && REFUSED.has(e.code) ? "refused" : "error");

/**
 * CLD-4 and CIN-4: the Load screen, for any application that loads a collection workbook.
 *
 * Download the template, choose the workbook, Check (which reads and writes nothing), then, only
 * when the check passes, Commit. BKDP only (CORE decision 6): the tab is not offered to a client
 * and the API refuses them. The file is sent again on commit, because it may have changed between
 * the two calls; commit re-runs the whole of check.
 *
 * One component, not two: a person who has loaded customers should not have to learn a second
 * screen to load channels, and a sentence improved here cannot drift out of one of them. What an
 * application supplies is its name, its words and its own dangerous action - never its own layout.
 */
export type ReplaceSection = {
  /** What replacing destroys, in this application's own nouns. */
  description: ReactNode;
  /**
   * The confirmation, which is the application's because the phrase and the counts are. ISO-3: it is handed the
   * check report the screen already holds, so it can say what the workbook would load without a second check.
   */
  dialog: (p: { open: boolean; busy: boolean; fileName: string | null; report: LoadCheckReport | null; onClose: () => void; onConfirm: (phrase: string) => void }) => ReactNode;
  /** What the button says when it cannot be used yet. */
  title?: string;
};

export type LoadScreenProps = {
  api: ApiClient;
  /** The application's route root, e.g. /clients/:id/apps/commercial-model. */
  base: string;
  canManage: boolean;
  /** The sentence a client reads instead of the screen. */
  maintained: string;
  appName: string;
  subtitle: string;
  templateFileName: string;
  /** What the template holds and when to download it again. */
  templateHelp: ReactNode;
  /** Step one's file when it is not the blank template: the path under `base` and the words on the card and the button. */
  template?: { path: string; title: string; button: string };
  replace?: ReplaceSection;
};

export default function LoadScreen({ api, base, canManage, maintained, appName, subtitle, templateFileName, templateHelp, template, replace }: LoadScreenProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [report, setReport] = useState<LoadCheckReport | null>(null);
  const [result, setResult] = useState<LoadCommitResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** CLD-9: open only once a check has passed. There is no "replace anyway". */
  const [replacing, setReplacing] = useState(false);
  const [replaced, setReplaced] = useState<{ deleted: Record<string, number>; notLoaded: number; snapshot?: { stored: boolean; fileName: string } } | null>(null);
  const last = useApiQuery<LastLoad | null>(() => (canManage ? api.request<LastLoad | null>(`${base}/load/last`) : Promise.resolve(null)), [api, base, canManage]);

  /**
   * ISO-3, CLD-9's precedent: a commit that would break something is refused with a confirmation it names (a header
   * and a count); the person confirms once, explicitly, and the file is sent again with it. The count travels, so a
   * confirmation given for two deletions does not cover three.
   */
  const [confirming, setConfirming] = useState<{ message: string; header: string; value: string; action: string } | null>(null);
  if (!canManage) return <EmptyState eyebrow="Load" title="The load is BKDP's." description={maintained} />;

  const send = (path: string, extra: Record<string, string> = {}) => api.request<LoadCheckReport | LoadCommitResult>(`${base}${path}`, { method: "POST", body: file, headers: { "Content-Type": XLSX, "X-File-Name": encodeURIComponent(file?.name ?? "workbook.xlsx"), ...extra } });
  const choose = (f: File | null) => { setFile(f); setReport(null); setResult(null); setReplaced(null); setError(null); setPhase("idle"); };
  const check = async () => {
    if (!file) return;
    setPhase("checking"); setError(null); setResult(null);
    try {
      const r = (await send("/load/check")) as LoadCheckReport;
      setReport(r); setPhase(r.canCommit ? "passed" : "failed");
    } catch (e) { setError(errorMessage(e)); setPhase(phaseFor(e)); }
  };
  const commit = async (confirmation?: { header: string; value: string }) => {
    if (!file || (phase !== "passed" && !confirmation)) return;
    setConfirming(null); setPhase("committing"); setError(null);
    try {
      const r = (await send("/load/commit", confirmation ? { [confirmation.header]: confirmation.value } : {})) as LoadCommitResult;
      setResult(r); setReport(r.report); setPhase("committed"); void last.reload();
    } catch (e) {
      const c = e instanceof ApiError && e.code === "confirmation_required" ? (e.details?.confirm as { header?: string; value?: string; action?: string } | undefined) : undefined;
      if (c?.header && c.value) { setConfirming({ message: e instanceof Error ? e.message : String(e), header: c.header, value: c.value, action: c.action ?? "Confirm and load" }); setPhase("passed"); return; }
      setError(errorMessage(e)); setPhase(phaseFor(e));
    }
  };
  const rejected = report?.sheets.flatMap((s) => s.rejected) ?? [];
  const warnings = report?.warnings ?? [];
  const words: Record<Phase, string> = {
    idle: file ? "Ready to check." : "No file chosen.", checking: "Checking… reading every row.",
    passed: warnings.length > 0 ? "Check passed with warnings: nothing was written. Commit to load it." : "Check passed: nothing was written. Commit to load it.",
    failed: "Check failed: fix the rejected rows and check again.", committing: "Committing…", committed: "Committed.",
    replacing: "Replacing everything…", replaced: "Replaced.", refused: "The file was not accepted.", error: "Something went wrong."
  };

  /** CLD-9 criterion 8: the snapshot the replacement kept, fetched as a signed link and opened. */
  const downloadSnapshot = async () => {
    try {
      const r = await api.request<{ url: string }>(`${base}/load/snapshot`);
      window.open(r.url, "_blank", "noopener");
    } catch (e) { setError(errorMessage(e)); }
  };

  /**
   * CLD-9: the delete and the load are one request and one transaction. A failure at any point
   * leaves the client's data exactly as it was, and the screen says so in those words.
   */
  const replaceAll = async (phrase: string) => {
    if (!file || phase !== "passed") return;
    setReplacing(false); setPhase("replacing"); setError(null);
    try {
      const r = await api.request<LoadCommitResult & { deleted: Record<string, number>; notLoaded: number; snapshot?: { stored: boolean; fileName: string } }>(`${base}/load/replace`, {
        method: "POST", body: file,
        headers: { "Content-Type": XLSX, "X-File-Name": encodeURIComponent(file.name), "X-Confirm": phrase }
      });
      setResult(r); setReport(r.report); setReplaced({ deleted: r.deleted, notLoaded: r.notLoaded, snapshot: r.snapshot }); setPhase("replaced"); void last.reload();
    } catch (e) { setError(errorMessage(e)); setPhase(phaseFor(e)); }
  };

  return (
    <Stack spacing={3}>
      <PageHeader eyebrow={appName} title="Load" subtitle={subtitle} />
      <SectionCard eyebrow="Step 1" title={template?.title ?? "Download the template"}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={() => void api.download(`${base}${template?.path ?? "/template"}`, templateFileName)}>{template?.button ?? "Download the template"}</Button>
          <Typography variant="body2" color="text.secondary">{templateHelp}</Typography>
        </Stack>
      </SectionCard>
      <SectionCard eyebrow="Step 2" title="Check, then commit">
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={() => fileInput.current?.click()} disabled={phase === "checking" || phase === "committing"}>Choose file</Button>
            <input ref={fileInput} type="file" accept=".xlsx" hidden onChange={(e) => choose(e.target.files?.[0] ?? null)} />
            {/* 3.2: the chip already says when no file is chosen; this line is the file's name, once there is one. */}
            {file && <Typography variant="body2" data-testid="file-name">{file.name}</Typography>}
            {/* The row rule: the phase is a sentence, coloured only when something has to be acted on (a failed or refused check, an error). */}
            <Typography variant="body2" component="span" data-testid="load-phase" role="status"><RowState label={words[phase]} acting={phase === "failed" || phase === "refused" || phase === "error"} /></Typography>
          </Stack>
          <Stack direction="row" spacing={3} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <Box>
              <Button variant="contained" onClick={() => void check()} disabled={!file || phase === "checking" || phase === "committing"}>Check</Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Check reads your workbook and writes nothing.</Typography>
            </Box>
            <Box>
              <Button variant="contained" color="secondary" onClick={() => void commit()} disabled={phase !== "passed"}>Commit</Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Loading again updates the rows you changed. It does not create duplicates.</Typography>
            </Box>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {/*
            * CIN-4 criterion 4: above the counts and in its own block, because a warning is
            * something the load WILL do that is worth reading before committing - not a rejection.
            */}
          {warnings.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Warning: {warnings.length === 1 ? "one row" : `${warnings.length} rows`} to read before you commit. Warnings do not stop a commit.</Typography>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
                {warnings.map((w) => (
                  <Typography key={`${w.sheet}-${w.row}-${w.message}`} component="li" variant="body2">
                    {w.sheet} row {w.row}: {w.message}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
          {report && (
            <DataTable
              sortable={false}
              columns={[{ key: "sheet", label: "Sheet" }, { key: "read", label: "Rows read" }, { key: "create", label: "To create" }, { key: "update", label: "To update" }, { key: "unchanged", label: "Unchanged" }, { key: "rejected", label: "Rejected" }]}
              rows={report.sheets.map((s) => ({ sheet: s.present ? s.sheet : `${s.sheet} (not in the file)`, read: s.rowsRead, create: s.toCreate, update: s.toUpdate, unchanged: s.unchanged, rejected: s.rejected.length }))}
            />
          )}
          {rejected.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{rejected.length} row{rejected.length === 1 ? "" : "s"} rejected. Fix each cell and check again; nothing is loaded while any row is rejected.</Typography>
              <DataTable
                columns={[{ key: "sheet", label: "Sheet" }, { key: "row", label: "Row" }, { key: "column", label: "Column" }, { key: "reason", label: "Reason" }]}
                rows={rejected.map((r) => ({ sheet: r.sheet, row: r.row, column: r.column ?? "", reason: r.reason }))}
              />
            </Box>
          )}
          {result && (
            <Alert severity="success">
              Committed {result.report.fileName}: {Object.entries(result.results).map(([sheet, r]) => `${sheet} ${r.created} created, ${r.updated} updated, ${r.unchanged} unchanged`).join("; ")}.
              {result.overrides.length > 0 && ` ${result.overrides.length} value${result.overrides.length === 1 ? "" : "s"} edited in the portal since the last load were left alone: ${result.overrides.map((o) => `${o.key} ${o.label}`).join(", ")}.`}
            </Alert>
          )}
        </Stack>
      </SectionCard>
      {/*
        * CLD-9 and CIN-5: below Check and Commit and visually apart from them, because it is not a
        * third step of the same thing. It becomes available only once a check has passed.
        */}
      <ConfirmDialog open={confirming !== null} title={confirming?.action ?? "Confirm"} description={confirming?.message} confirmLabel={confirming?.action ?? "Confirm"} destructive busy={phase === "committing"}
        onClose={() => setConfirming(null)} onConfirm={() => { if (confirming) void commit({ header: confirming.header, value: confirming.value }); }} />
      {replace && (
        <SectionCard eyebrow="Start fresh" title="Replace everything">
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">{replace.description}</Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button variant="outlined" color="error" onClick={() => setReplacing(true)} disabled={phase !== "passed"}>Replace everything</Button>
              <Typography variant="caption" color="text.secondary">
                {phase === "passed" ? "The check passed, so this is available." : "Choose a file and check it first."}
              </Typography>
            </Stack>
            {replaced && (
              <Alert severity="success">
                <Stack spacing={1} alignItems="flex-start">
                  <span>
                    Replaced everything from {result?.report.fileName}. Deleted{" "}
                    {Object.entries(replaced.deleted).filter(([k, n]) => n > 0 && k !== "peopleKept").map(([k, n]) => `${n} ${k}`).join(", ") || "nothing"}
                    , then loaded {Object.entries(result?.results ?? {}).reduce((n, [, r]) => n + r.created, 0)} rows.
                  </span>
                  {/* CLD-9 criterion 8: what was there a moment ago, as a workbook this screen can load back. */}
                  {replaced.snapshot?.stored
                    ? <Button size="small" variant="outlined" onClick={() => void downloadSnapshot()}>Download what was there before</Button>
                    : <span>No snapshot was kept: file storage is not configured for this environment.</span>}
                </Stack>
              </Alert>
            )}
          </Stack>
        </SectionCard>
      )}

      <SectionCard eyebrow="History" title="Last load">
        {last.data ? (
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              {/* CLD-9 criterion 10: its own word and its own colour, so the history reads honestly. */}
              <Typography variant="body2" component="span" data-testid="last-load-kind"><RowState label={last.data.kind === "replacement" ? "Replacement" : "Load"} acting={last.data.kind === "replacement"} /></Typography>
              <Typography variant="body2">
                {last.data.fileName} on {formatDate(last.data.at)} by {last.data.by ?? "BKDP"}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {last.data.kind === "replacement" && last.data.deleted
                ? `Deleted ${Object.entries(last.data.deleted).filter(([k, n]) => n > 0 && k !== "peopleKept").map(([k, n]) => `${n} ${k}`).join(", ") || "nothing"}, then loaded: `
                : ""}
              {Object.entries(last.data.sheets ?? {}).map(([sheet, r]) => `${sheet} ${r.created} created, ${r.updated} updated`).join("; ")}.
            </Typography>
          </Stack>
        ) : <Typography variant="body2" color="text.secondary">Nothing has been loaded yet.</Typography>}
      </SectionCard>

      {replace?.dialog({ open: replacing, busy: phase === "replacing", fileName: file?.name ?? null, report, onClose: () => setReplacing(false), onConfirm: (phrase) => void replaceAll(phrase) })}
    </Stack>
  );
}
